import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { organizationService } from '@/services/api/organization.service';
import { cn } from '@/lib/utils';
import { bookingPageApi, apiErrorMessage, type BookingPageAdmin, type BookingPageSettings } from './api';
import { applyDraft, emptyCustomHours, publicPageUrl } from './draft';
import { LinkCard } from './components/LinkCard';
import { PhotosCard } from './components/PhotosCard';
import { HeaderCard } from './components/HeaderCard';
import { FromAccountCard } from './components/FromAccountCard';
import { SectionsCard } from './components/SectionsCard';
import { PreviewPanel } from './components/PreviewPanel';
import { Segmented } from './components/primitives';

const QUERY_KEY = ['admin-booking-page'];

function normalize(settings: BookingPageSettings): BookingPageSettings {
  return { ...settings, custom_hours: { ...emptyCustomHours(), ...settings.custom_hours } };
}

// The desktop preview rail only mounts at lg+, so phones don't load a second iframe.
function useIsDesktop() {
  const query = '(min-width: 1024px)';
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return matches;
}

// Width of an element, for scaling the preview iframe.
function useWidth<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(340);
  useEffect(() => {
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, width] as const;
}

export default function AdminBookingPagePage() {
  const { t } = useTranslation('adminBookingPage');
  const { toast } = useToast();
  const { data: organization } = useQuery({ queryKey: ['organization'], queryFn: () => organizationService.getOrganization() });
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: QUERY_KEY, queryFn: bookingPageApi.get });
  const [draft, setDraft] = useState<BookingPageSettings | null>(null);
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const isDesktop = useIsDesktop();
  const [railRef, railWidth] = useWidth<HTMLDivElement>();
  const [mobileRef, mobileWidth] = useWidth<HTMLDivElement>();

  useEffect(() => {
    if (data && !draft) setDraft(normalize(data.settings));
  }, [data, draft]);

  const saved = useMemo(() => (data ? JSON.stringify(normalize(data.settings)) : ''), [data]);
  const dirty = !!draft && JSON.stringify(draft) !== saved;

  const setData = (next: BookingPageAdmin) => queryClient.setQueryData(QUERY_KEY, next);

  const save = useMutation({
    mutationFn: (settings: Partial<BookingPageSettings>) => bookingPageApi.update(settings),
    onSuccess: (next) => {
      setData(next);
      setDraft(normalize(next.settings));
      toast({ title: t('save.saved') });
    },
    onError: (error) => toast({ title: apiErrorMessage(error, t('save.failed')), variant: 'destructive' }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const upload = async (file: File) => {
    try {
      const photo = await bookingPageApi.uploadPhoto(file);
      queryClient.setQueryData<BookingPageAdmin>(QUERY_KEY, (old) => old && { ...old, photos: [...old.photos, photo] });
      toast({ title: t('photos.uploaded') });
    } catch (error) {
      toast({ title: apiErrorMessage(error, t('photos.uploadFailed')), variant: 'destructive' });
    }
  };

  const reorder = async (ids: string[]) => {
    queryClient.setQueryData<BookingPageAdmin>(QUERY_KEY, (old) => old && { ...old, photos: ids.map((id) => old.photos.find((p) => p.id === id)!).filter(Boolean) });
    try {
      const photos = await bookingPageApi.reorderPhotos(ids);
      queryClient.setQueryData<BookingPageAdmin>(QUERY_KEY, (old) => old && { ...old, photos });
    } catch (error) {
      toast({ title: apiErrorMessage(error, t('save.failed')), variant: 'destructive' });
      void refresh();
    }
  };

  const remove = async (id: string) => {
    await bookingPageApi.deletePhoto(id);
    queryClient.setQueryData<BookingPageAdmin>(QUERY_KEY, (old) => old && { ...old, photos: old.photos.filter((p) => p.id !== id) });
    toast({ title: t('photos.removed') });
  };

  if (isLoading || !data || !draft) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  const patch = (p: Partial<BookingPageSettings>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const timeZone = organization?.timezone;
  const block = applyDraft(data, draft, data.photos, timeZone);
  const url = publicPageUrl(data.organization.subdomain);

  const cards = (
    <div className="flex min-w-0 flex-1 flex-col gap-4 sm:gap-5">
      <LinkCard url={url} />
      <PhotosCard photos={data.photos} max={data.limits.max_photos} maxBytes={data.limits.max_photo_bytes} onUpload={upload} onReorder={reorder} onDelete={remove} />
      <HeaderCard organization={data.organization} tagline={draft.tagline} onTagline={(tagline) => patch({ tagline })} />
      <FromAccountCard counts={data.counts} />
      <SectionsCard draft={draft} data={data} timeZone={timeZone} onChange={patch} />
    </div>
  );

  return (
    <div className="flex flex-col gap-4 pb-40 sm:gap-6 lg:pb-28" data-testid="admin-booking-page">
      {/* Mobile: Edit / Preview tabs */}
      <div className="lg:hidden">
        <Segmented
          grow
          label={t('title')}
          value={tab}
          onChange={(v) => setTab(v as 'edit' | 'preview')}
          options={[
            { value: 'edit', label: <><Settings2 className="h-3.5 w-3.5" />{t('tabs.edit')}</> },
            { value: 'preview', label: <><Eye className="h-3.5 w-3.5" />{t('tabs.preview')}</> },
          ]}
        />
      </div>

      <div className="flex items-start gap-7">
        <div className={cn('min-w-0 flex-1', tab === 'preview' && 'hidden lg:block')}>{cards}</div>

        {isDesktop && (
          <div ref={railRef} className="sticky top-6 w-[380px] shrink-0">
            <PreviewPanel subdomain={data.organization.subdomain} block={block} publicUrl={url} width={railWidth - 38} />
          </div>
        )}
      </div>

      {!isDesktop && tab === 'preview' && (
        <div ref={mobileRef} className="lg:hidden" data-testid="mobile-preview">
          {dirty && <p className="mb-3 text-center text-body-sm font-medium text-brand-700">{t('preview.unsaved')}</p>}
          <PreviewPanel subdomain={data.organization.subdomain} block={block} publicUrl={url} width={mobileWidth - 38} mode="mobile" showToggle={false} />
        </div>
      )}

      {/* Save bar: fixed at the bottom on mobile, sticky under the content on desktop */}
      <div
        className="fixed inset-x-0 bottom-[calc(54px+env(safe-area-inset-bottom,0px))] z-30 flex flex-col gap-2 border-t border-line bg-surface/95 px-4 pb-3 pt-3 backdrop-blur lg:sticky lg:bottom-0 lg:-mx-8 lg:flex-row lg:items-center lg:px-8"
        data-testid="save-bar"
      >
        <span className={cn('inline-flex flex-1 items-center gap-2 text-body text-soft', !dirty && 'invisible')}>
          <span className="h-2 w-2 rounded-pill bg-warn" aria-hidden="true" />
          {t('save.unsaved')}
        </span>
        <div className="flex gap-2.5">
          <Button variant="outline" className="h-14 flex-1 lg:h-11 lg:flex-none" disabled={!dirty || save.isPending} onClick={() => setDraft(normalize(data.settings))}>
            {t('save.discard')}
          </Button>
          <Button className="h-14 flex-[2] lg:h-11 lg:flex-none" disabled={!dirty || save.isPending} onClick={() => { const { embed: _embed, ...settings } = draft; save.mutate(settings); }}>
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {save.isPending ? t('save.saving') : t('save.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
